import { type FC, useCallback, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";

import { type InputIdSelection, type InputRestriction } from "../../../graphql.js";
import { getTableDataSlice } from "../../../redux/modules/table-data.js";
import { type IAppState } from "../../../redux/reducer.js";
import { TableRestriction } from "../../../settings/settings.js";
import DataTableContainer, { type IDataTableContainerOwnProps } from "../../core/grid/DataTableContainer.js";
import { type ISelectionModel } from "@/app-utils/selection-model.ts";

interface DataTableRestrictionsProps extends IDataTableContainerOwnProps {
  enableTableRestrictions: boolean; // [SEL6-2556] do NOT enable the restrictions when outside of the GenomicFeatureTable context,
  // otherwise the TableRestriction.SELECTION restriction will be injected (which is the point of this component)
}

/**
 * This component does take care of enhancing the table spec restrictions with the selection model (TableRestriction = SELECTION) given a specific grid.
 * The FeaturesTablePane component does take care of the CONTIG/RANGE restrictions since these restrictions are common to all tables.
 * The Ref Genome Id restriction is provided by the GenomeViewerGrid component, because it needs to cross check the selected ref genome with the genome analysis group specifically in case of variant related tables.
 * However it would be possible to add all restrictions in one single place like here, or using an equivalent hook.
 */
const DataTableRestrictions: FC<DataTableRestrictionsProps> = (props) => {
  const tableDataSelectionModel = useSelector<IAppState, ISelectionModel>(
    (state) => getTableDataSlice(state.tableData, props.tableSpec)?.selectionModel,
    shallowEqual,
  );
  const rowRestriction = useSelector<IAppState, TableRestriction>(
    (state) => state.tableData.rowRestriction,
    shallowEqual,
  );

  const getSelectionModelRestriction = useCallback(() => {
    if (props.enableTableRestrictions && rowRestriction === TableRestriction.SELECTION) {
      return {
        selectedResultIds: (tableDataSelectionModel?.selectedIds as number[]) ?? [],
      } satisfies InputIdSelection;
    }
    return null;
  }, [tableDataSelectionModel, rowRestriction, props.enableTableRestrictions]);

  // restrictions from props but amended with the selection model restrictions
  const [tableSelectionRestriction, setTableSelectionRestriction] =
    useState<InputIdSelection>(getSelectionModelRestriction());

  const debouncedUpdateTableRestrictionsState = useDebouncedCallback(() => {
    setTableSelectionRestriction(getSelectionModelRestriction());
  }, 2000);

  useEffect(() => {
    setTableSelectionRestriction(getSelectionModelRestriction());
  }, [rowRestriction]);

  useEffect(() => {
    debouncedUpdateTableRestrictionsState();
  }, [tableDataSelectionModel]);

  // merge upstream computed restrictions
  const tableSpecRestrictions: InputRestriction = tableSelectionRestriction
    ? { ...props.tableSpec.restrictions, selection: tableSelectionRestriction }
    : props.tableSpec.restrictions;

  return <DataTableContainer {...props} tableSpec={{ ...props.tableSpec, restrictions: tableSpecRestrictions }} />;
};

export default DataTableRestrictions;
export type { DataTableRestrictionsProps };
